import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNowStrict } from "date-fns";
import { RefreshCw, Search } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SlackAppAdminService } from "@/services/admin/SlackAppAdminService";
import type { AdminSlackApp, SlackAppHealth } from "@/types/admin/slack-apps";
import { SLACK_APP_HEALTH } from "./healthMeta";
import { SlackAppDetailsSheet } from "./SlackAppDetailsSheet";

const COLUMN_COUNT = 6;

function SummaryTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white rounded-lg border border-genie-border p-4">
      <p className="text-sm text-genie-text-secondary">{label}</p>
      <p className="text-2xl font-bold text-genie-text-primary mt-1">{value}</p>
    </div>
  );
}

/** Every genie's own Slack app and whether it's working, worst first. Fixes happen in the genie's Share drawer. */
const SlackAppsPage = () => {
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState<SlackAppHealth | "all">("all");
  const [openAppId, setOpenAppId] = useState<string | null>(null);

  const { data: apps = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ["admin-slack-apps"],
    queryFn: () => SlackAppAdminService.list(),
    staleTime: 30 * 1000,
  });

  const visibleApps = useMemo(() => {
    const term = search.trim().toLowerCase();
    return apps.filter(
      (app) =>
        (healthFilter === "all" || app.health === healthFilter) &&
        (!term ||
          [app.genie_name, app.owner_email, app.slack_app_id].some((value) => value?.toLowerCase().includes(term))),
    );
  }, [apps, search, healthFilter]);

  const healthyCount = apps.filter((app) => app.health === "healthy").length;
  const openApp: AdminSlackApp | null = apps.find((app) => app.id === openAppId) ?? null;

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        <PageHeader
          title="Custom Slack Apps"
          genieIcon="genie-lamp"
          subtitle="Genies running on their owner's own Slack app, problems first"
          action={
            <Button onClick={() => refetch()} variant="outline" className="gap-2 min-h-[44px]" disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <SummaryTile label="Total" value={apps.length} />
          <SummaryTile label="Healthy" value={healthyCount} />
          <SummaryTile label="Need attention" value={apps.length - healthyCount} />
        </div>

        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search by genie, owner email or Slack App ID..."
                className="pl-10 text-base sm:text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={healthFilter} onValueChange={(value) => setHealthFilter(value as SlackAppHealth | "all")}>
              <SelectTrigger className="text-base sm:text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(Object.keys(SLACK_APP_HEALTH) as SlackAppHealth[]).map((health) => (
                  <SelectItem key={health} value={health}>
                    {SLACK_APP_HEALTH[health].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-genie-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Genie</TableHead>
                <TableHead className="hidden md:table-cell">Owner</TableHead>
                <TableHead className="hidden lg:table-cell">Slack App ID</TableHead>
                <TableHead className="hidden sm:table-cell">Workspaces</TableHead>
                <TableHead className="hidden md:table-cell">Last heard from Slack</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-8 text-genie-text-secondary">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                    Checking on the apps...
                  </TableCell>
                </TableRow>
              ) : visibleApps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-8 text-genie-text-muted">
                    {apps.length === 0 ? "No genie has its own Slack app yet" : "No apps match these filters"}
                  </TableCell>
                </TableRow>
              ) : (
                visibleApps.map((app) => {
                  const health = SLACK_APP_HEALTH[app.health];
                  return (
                    <TableRow
                      key={app.id}
                      onClick={() => setOpenAppId(app.id)}
                      className="cursor-pointer hover:bg-genie-primary/5"
                    >
                      <TableCell className="text-sm font-medium text-genie-text-primary">
                        {app.genie_name ?? "Deleted genie"}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-genie-text-secondary">
                        {app.owner_email ?? "—"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-genie-text-secondary">
                        {app.slack_app_id ?? "—"}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-genie-text-secondary">
                        {app.workspaces.length}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-genie-text-secondary whitespace-nowrap">
                        {app.last_event_at
                          ? formatDistanceToNowStrict(new Date(app.last_event_at), { addSuffix: true })
                          : "Never"}
                      </TableCell>
                      <TableCell>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${health.badge}`}>
                          {health.label}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <SlackAppDetailsSheet app={openApp} onClose={() => setOpenAppId(null)} />
    </div>
  );
};

export default SlackAppsPage;
